import React, { useState, useEffect } from 'react';
import './OrdersTable.css';
import './RequisitesTable.css';
import RequisiteDetailsPanel from './RequisiteDetailsPanel';
import AddRequisiteModal from './AddRequisiteModal';
import Pagination from './Pagination';
import { fetchDevicesData, fetchTradersData } from '../services/dataApi';
import useUrlDetails from '../hooks/useUrlDetails';

const RequisitesTable = ({ data, onAdd, onEdit, onAddClick, showTraderName = false, pagination, onPageChange }) => {
  const [copiedId, setCopiedId] = useState(null);
  const [editingRequisite, setEditingRequisite] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [traders, setTraders] = useState([]);
  const [tradersLoading, setTradersLoading] = useState(false);

  // Load the device list to display names
  useEffect(() => {
    const loadDevices = async () => {
      setDevicesLoading(true);
      try {
        // Load all devices (large limit to fetch all of them)
        const devicesData = await fetchDevicesData(1, 1000);
        // Extract the device array from the response
        let devicesList = [];
        
        // Support the new paginated format
        if (devicesData && devicesData.data && Array.isArray(devicesData.data)) {
          devicesList = devicesData.data;
        } else if (Array.isArray(devicesData)) {
          devicesList = devicesData;
        } else if (devicesData && devicesData.ops && devicesData.ops.length > 0) {
          const firstOp = devicesData.ops[0];
          if (firstOp.list && Array.isArray(firstOp.list)) {
            devicesList = firstOp.list;
          } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
            devicesList = firstOp.data.list;
          }
        }
        setDevices(devicesList);
      } catch (err) {
        console.error('Error loading devices:', err);
        setDevices([]);
      } finally {
        setDevicesLoading(false);
      }
    };
    
    loadDevices();
  }, []);

  // Load the trader list if the trader name needs to be shown
  useEffect(() => {
    if (showTraderName) {
      const loadTraders = async () => {
        setTradersLoading(true);
        try {
          // Load all traders (large limit to fetch all of them)
          const tradersData = await fetchTradersData(1, 1000);
          let tradersList = [];
          
          // Support the new paginated format
          if (tradersData && tradersData.data && Array.isArray(tradersData.data)) {
            tradersList = tradersData.data;
          } else if (Array.isArray(tradersData)) {
            tradersList = tradersData;
          } else if (tradersData && tradersData.ops && tradersData.ops.length > 0) {
            const firstOp = tradersData.ops[0];
            if (firstOp.list && Array.isArray(firstOp.list)) {
              tradersList = firstOp.list;
            } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
              tradersList = firstOp.data.list;
            }
          }
          setTraders(tradersList);
        } catch (err) {
          console.error('Error loading traders:', err);
          setTraders([]);
        } finally {
          setTradersLoading(false);
        }
      };
      
      loadTraders();
    }
  }, [showTraderName]);

  // Function to get the trader name by traderId
  const getTraderNameById = (traderId) => {
    if (!traderId) return '-';
    const trader = traders.find(t => {
      const id = t.traderId || t.TraderId || t.TRADER_ID || t.ref;
      return id === traderId;
    });
    if (trader) {
      return trader.name || trader.Name || trader.NAME || trader.login || trader.Login || traderId;
    }
    return traderId;
  };

  // Function to get the device name by ref
  const getDeviceNameByRef = (deviceRef) => {
    if (!deviceRef) return '-';
    const device = devices.find(d => (d.ref || d.id) === deviceRef);
    if (device) {
      return device.deviceName || device.DeviceName || device.device_name || deviceRef;
    }
    return deviceRef; // If not found, show the ref
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Extract data - support both the new paginated format and the old format
  let requisites = [];
  
  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    requisites = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    requisites = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with the old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      requisites = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      requisites = firstOp.data.list;
    }
  }

  const [selectedRequisite, setSelectedRequisite] = useUrlDetails(requisites, 'requisiteId');

  // Sort by creation date (create_time) - newest first
  requisites.sort((a, b) => {
    const timeA = a.Created || a.created || a.create_time || 0;
    const timeB = b.Created || b.created || b.create_time || 0;
    // Sort in descending order (newest first)
    return timeB - timeA;
  });

  if (requisites.length === 0) {
    return (
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Requisites List (0)</h3>
          {onAddClick && (
            <button 
              className="add-button"
              onClick={onAddClick}
            >
              Add
            </button>
          )}
        </div>
        <div className="orders-empty">
          <p>No data to display</p>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp);
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '1rem', fontWeight: 600 }}>{day}.{month}.{year}</span>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  // Determine the columns to display
  const displayColumns = [
    { 
      key: 'Created', 
      label: 'Created', 
      getValue: (requisite) => {
        const created = requisite.Created || requisite.created || requisite.create_time || null;
        return formatDate(created);
      }
    },
    ...(showTraderName ? [{
      key: 'TraderName',
      label: 'Trader Name',
      getValue: (requisite) => {
        const traderId = requisite.traderId || requisite.TraderId || requisite.TRADER_ID;
        return tradersLoading ? 'Loading...' : getTraderNameById(traderId);
      }
    }] : []),
    { 
      key: 'NameSurname', 
      label: 'Name Surname', 
      getValue: (requisite) => {
        return requisite.NameSurname || requisite.nameSurname || requisite.name_surname || '-';
      }
    },
    { 
      key: 'Bank', 
      label: 'Bank', 
      getValue: (requisite) => {
        return requisite.Bank || requisite.bank || '-';
      }
    },
    { 
      key: 'AccountNumber', 
      label: 'Account Number', 
      getValue: (requisite) => {
        const accountNumber = requisite.AccountNumber || requisite.accountNumber || requisite.account_number || '-';
        if (accountNumber === '-') return '-';
        
        const isCopied = copiedId === accountNumber;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>{accountNumber}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(accountNumber, accountNumber);
              }}
              className="copy-button"
              title={isCopied ? 'Copied!' : 'Copy to clipboard'}
            >
              {isCopied ? '✓' : '📋'}
            </button>
          </div>
        );
      }
    },
    { 
      key: 'Currency', 
      label: 'Currency', 
      getValue: (requisite) => {
        return requisite.Currency || requisite.currency || '-';
      }
    },
    { 
      key: 'Device', 
      label: 'Device', 
      getValue: (requisite) => {
        const deviceRef = requisite.device || requisite.Device;
        return devicesLoading ? 'Loading...' : getDeviceNameByRef(deviceRef);
      }
    },
    { 
      key: 'Status', 
      label: 'Status', 
      getValue: (requisite) => {
        return requisite.status || requisite.Status || requisite.STATUS || '-';
      }
    }
  ];

  const totalItems = pagination?.total || requisites.length;

  return (
    <>
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Requisites List ({totalItems})</h3>
          {onAddClick && (
            <button 
              className="add-button"
              onClick={onAddClick}
            >
              Add
            </button>
          )}
        </div>
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th key={column.key} className={`header-${column.key.toLowerCase()}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requisites.map((requisite, index) => {
                const status = (requisite.status || requisite.Status || '').toLowerCase();
                return (
                  <tr 
                    key={requisite.id || requisite.ref || index}
                    className="order-row"
                    onClick={() => setSelectedRequisite(requisite)}
                  >
                    {displayColumns.map((column) => {
                      const isStatusColumn = column.key === 'Status';
                      const statusClass = isStatusColumn ? `status-${status}` : '';
                      return (
                        <td 
                          key={column.key}
                          className={`column-${column.key.toLowerCase()} ${statusClass}`}
                          data-label={column.label}
                        >
                          {column.getValue(requisite)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      {selectedRequisite && (
        <RequisiteDetailsPanel 
          requisite={selectedRequisite} 
          devices={devices}
          devicesLoading={devicesLoading}
          onClose={() => setSelectedRequisite(null)}
          onEdit={onEdit ? (requisite) => {
            setEditingRequisite(requisite);
            setSelectedRequisite(null);
          } : undefined}
        />
      )}
      {onEdit && <AddRequisiteModal
        isOpen={!!editingRequisite}
        onClose={() => setEditingRequisite(null)}
        onSubmit={onEdit}
        initialData={editingRequisite}
        isEdit={true}
      />}
    </>
  );
};

export default RequisitesTable;
